<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiAvatar, UiIcon, UiNavLink, generateInitials, getExpertTypeDisplay } from "@shopexperts/shared";
import type { AdminLeadQueueRow, ProjectStatus } from "@shopexperts/legacy-api";
import ProjectOpenLinkLabel from "@/v2Projects/components/ProjectOpenLinkLabel.vue";
import {
  getStatusConfig,
  formatCurrency,
  formatRelativeTime,
} from "@/v2Projects/utils/display";

const props = defineProps<{
  row: AdminLeadQueueRow;
  compact?: boolean;
}>();

const project = computed(() => props.row.project);
const projectRoute = computed(() => project.value
  ? {
      name: "admin-project-detail",
      params: { id: String(project.value.id) },
    }
  : null);
const rootComponent = computed(() => project.value ? UiNavLink : "div");
const rootProps = computed(() => projectRoute.value ? { to: projectRoute.value } : {});
const status = computed(() => {
  if (!project.value) {
    return {
      label: "Awaiting Match",
      badgeClass: "bg-yellow-100 text-yellow-800",
      accentBorder: "border-l-yellow-300",
      avatarRing: "ring-yellow-100",
    };
  }

  return getStatusConfig(project.value.project_status as ProjectStatus, project.value.project_type);
});

const expert = computed(() => props.row.matched_expert ?? props.row.selected_expert);
const expertName = computed(() => expert.value?.name ?? "Unassigned");
const expertInitials = computed(() => generateInitials(expertName.value));
const expertAvatarUrl = computed(() => expert.value?.avatar ?? undefined);

const expertTypeLabel = computed(() => {
  const value = expert.value;
  if (!value) return "";
  return getExpertTypeDisplay(value);
});

const activeQuote = computed(() => props.row.latest_quote ?? null);
const activeQuoteCurrency = computed(
  () => (activeQuote.value as { currency?: string | null } | null)?.currency ?? null,
);

const attention = computed(() => {
  const value = props.row.attention;
  if (!value) return null;
  const dayCount = `${value.waiting_days} day${value.waiting_days === 1 ? "" : "s"}`;
  const days = value.reason === "expert"
    ? `Assigned ${dayCount} ago, no expert reply`
    : `Waiting ${dayCount} on client`;
  const alerted = value.last_alerted_at
    ? `Flagged in Slack ${new Intl.DateTimeFormat("en", { month: "short", day: "numeric" }).format(new Date(value.last_alerted_at))}`
    : "Not flagged in Slack yet";
  return { days, alerted };
});

const lastMessage = computed(() => {
  if (!project.value?.description) return null;
  return {
    text: project.value.description,
    sender: "Project",
    time: project.value.created_at ? formatRelativeTime(project.value.created_at) : null,
  };
});
</script>

<template>
  <component
    :is="rootComponent"
    v-bind="rootProps"
    class="flex items-start gap-3 px-5 py-3 border-l-[3px] border-l-transparent transition-colors duration-150 group/ui-button no-underline text-inherit"
    :class="[
      status.accentBorder,
      {
        'py-2': compact,
        'cursor-pointer hover:bg-gray-50': project,
        'cursor-default hover:bg-transparent': !project,
      },
    ]"
  >
    <div
      class="shrink-0 rounded-full bg-white p-[2px] ring-2"
      :class="status.avatarRing"
    >
      <UiAvatar
        :url="expertAvatarUrl"
        :initials="expertInitials"
        :name="expertName"
        size="xs"
        :responsive="false"
        color-coded
      />
    </div>

    <div class="min-w-0 flex-1 lg:flex-initial lg:min-w-64">
      <div class="flex items-center gap-2 min-w-0 flex-wrap">
        <UiText as="span" variant="p-small" class="font-semibold text-gray-900 truncate">
          {{ expertName }}
        </UiText>
        <span
          :class="status.badgeClass"
          class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide shrink-0"
        >
          {{ status.label }}
        </span>
        <span
          v-if="attention"
          class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide shrink-0 bg-amber-100 text-amber-800"
        >
          {{ attention.days }}
        </span>
        <UiText v-if="attention" as="span" variant="p-small" class="text-gray-500 shrink-0">
          {{ attention.alerted }}
        </UiText>
      </div>

      <div class="flex items-center gap-1.5 mt-0.5 flex-wrap">
        <UiText v-if="expertTypeLabel" as="span" variant="p-small" class="truncate max-w-48 text-gray-600">
          {{ expertTypeLabel }}
        </UiText>
        <template v-if="expert?.email">
          <span class="text-gray-400">&middot;</span>
          <UiText as="span" variant="p-small" class="text-gray-600 truncate">
            {{ expert.email }}
          </UiText>
        </template>
      </div>

      <div v-if="lastMessage" class="flex items-center gap-1.5 mt-1.5">
        <UiIcon name="chat-bubble" class="w-3 h-3 text-gray-500 shrink-0" />
        <UiText as="span" variant="p-small" class="text-gray-600 min-w-0" :truncate="1">
          <span class="font-medium text-gray-800">{{ lastMessage.sender }}:</span>
          {{ lastMessage.text }}
        </UiText>
        <UiText v-if="lastMessage.time" as="span" variant="p-small" class="text-gray-500 shrink-0">
          &middot; {{ lastMessage.time }}
        </UiText>
      </div>
      <UiText v-else-if="project?.title" as="p" variant="p-small" class="mt-1.5 line-clamp-1 text-gray-600 min-w-0">
        {{ project.title }}
      </UiText>
      <UiText v-else as="p" variant="p-small" class="mt-1.5 line-clamp-1 text-gray-600 min-w-0">
        No linked project yet.
      </UiText>
    </div>

    <div class="hidden md:flex ml-auto items-center self-center shrink-0">
      <div v-if="activeQuote" class="hidden lg:flex shrink-0 mr-8 xl:mr-12">
        <UiText as="span" variant="p-small" class="font-medium text-gray-900">
          {{ formatCurrency(activeQuote.amount_cents, activeQuoteCurrency) }}
        </UiText>
      </div>

      <div class="shrink-0">
        <ProjectOpenLinkLabel v-if="project" />
      </div>
    </div>
  </component>
</template>
